import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import axios from 'axios';
import AuthCard from './AuthCard';
import { authService } from '../../services/api';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CloseOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

const ForgotPasswordAuthCard = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('form'); // form | sent | external
    const [message, setMessage] = useState({ type: '', text: '' });
    const [logoUrl, setLogoUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [clientSlug, setClientSlug] = useState(null);
    

    useEffect(() => {
        const fetchLogo = async () => {
        try {
            const res = await axios.get('/api/settings/logo');
            if (res.data.success && res.data.logoUrl) {
                setLogoUrl(res.data.logoUrl);
            }
            else 
            {                
                setLogoUrl('/images/beetsol-logo.png');
            }
            setClientSlug(res.data.slug || null);
        } catch (err) {
            console.error('Failed to fetch logo', err);
        }
        };

        fetchLogo();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (!email.trim()) {
        setMessage({ type: 'error', text: 'Please enter a valid email address' });
        return;
        }

        setIsLoading(true);

        try {
            const check = await authService.forgotPasswordCheck(email);

            if (check.data.pageStatus === 'external-signin') {
                setStatus('external');
                return;
            }
            else if (check.data.pageStatus === 'inactive') {
                setStatus('inactive');
                return;
            }
            else if (check.data.pageStatus === 'invited') {
                setStatus('invited');
                return;
            }
            else if (check.data.pageStatus === 'invited-external') {
                setStatus('invited-external');
                return;
            }

            const response = await authService.forgotPasswordNew(email,true);

            if (response.data.email) {
                setEmail(response.data.email);
            }
            setStatus('sent');
        } catch (error) {
            // A switched-off workspace or an ended free trial is refused with its message (S-14, K11 pt 2), shown on the
            // form, as nothing is sent. Removed 17 Oct 2026 (S-14): showing "Check your email" for it too. So is a request
            // over the step's limits (429, S-6.2 pt 5). Removed 2 Nov 2026 (S-6.2 pt 5): "Check your email" for it.
            const refusedWith = error.response && [403, 429].includes(error.response.status) && error.response.data
                ? error.response.data.message : null;
            if (refusedWith) {
                setMessage({ type: 'error', text: refusedWith });
            } else {
                setStatus('sent');
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (status === 'sent') {
        return (
        <AuthCard logoUrl={logoUrl}>
            <div className="ac-top">
                <div className="ac-icon ac-icon-success">
                    <span style={{ fontSize: '24px' }}>
                        <EmailOutlinedIcon />
                    </span>
                </div>
                <div className="ac-title">Check your email</div>
                <div className="ac-sub">
                    If an account exists for <b>{email}</b> email, we’ve sent a password reset link. It expires in 30 minutes.
                </div>
            </div>
            <div className="ac-body">
                <div className="ac-field-link">
                    Didn’t receive it? <Link className="ac-link" onClick={handleSubmit}>Resend</Link>
                </div>
                <div className="ac-field-link">
                    <Link to={`/learn/${clientSlug}`} onClick={() => setStatus('email-input')} className="ac-link">
                        ← Back to sign in
                    </Link>
                </div>
            </div>
            <div className="ac-footer" />
        </AuthCard>
        );
    }

    if (status === 'external') {
        return (
        <AuthCard logoUrl={logoUrl}>
            <div className="ac-top">
                <div className="ac-icon ac-icon-success">
                    <span style={{ fontSize: '24px' }}>
                        <EmailOutlinedIcon />
                    </span>
                </div>
                <div className="ac-title">External accounts use magic links</div>
                <div className="ac-sub">
                    Your account doesn’t use a password. Go to the sign in page and we’ll send you a magic link instead.
                </div>
            </div>
            <div className="ac-body">                                   
                <button className="ac-btn-primary" onClick={() => navigate(`/learn/${clientSlug}`)}>
                    Go to sign in
                </button>
            </div>
            <div className="ac-footer" />
        </AuthCard>
        );
    }

    // An Invited internal user sees the sign-in page's invitation words (LearnerLoginCard), and the page's check sends the
    // invitation again, as the sign-in page's email step does (S-6.1 pt 5, agreed 17 Oct). Removed 17 Oct 2026 (S-6.1
    // pt 5): showing them the inactive card.
    if (status === 'invited') {
        return (
            <AuthCard logoUrl={logoUrl}>
                <div className="ac-top">
                    <div className="ac-icon ac-icon-warn">
                    <span style={{ fontSize: '24px' }}>
                        <WarningAmberOutlinedIcon />
                    </span>
                    </div>
                    <div className="ac-title">Activation required</div>
                    <div className="ac-sub">Your account is pending activation</div>
                </div>
                <div className="ac-body">
                    <div className="ac-box ac-box-warn">
                    <div className="ac-box-title">Invitation pending</div>
                    <div className="ac-box-sub">We've sent an invitation to <strong>{email}</strong>. Click the link in your email to activate your account and set a password.</div>
                    </div>
                    <div className="ac-field-link">
                    <Link to={`/learn/${clientSlug}`} onClick={() => setStatus('email-input')} className="ac-link">
                        ← Back to sign in
                    </Link>
                    </div>
                </div>
                <div className="ac-footer" />
            </AuthCard>
        );
    }

    // An Invited external user sees the sign-in page's invitation words for an external account (LearnerLoginCard), and
    // the page's check sends the link again, as the sign-in page's email step does (S-6.1 pt 5, agreed 18 Oct). Removed
    // 18 Oct 2026 (S-6.1 pt 5): showing them the inactive card.
    if (status === 'invited-external') {
        return (
            <AuthCard logoUrl={logoUrl}>
                <div className="ac-top">
                    <div className="ac-icon ac-icon-warn">
                    <span style={{ fontSize: '24px' }}>
                        <WarningAmberOutlinedIcon />
                    </span>
                    </div>
                    <div className="ac-title">Invitation pending</div>
                    <div className="ac-sub">Your account was created but hasn’t been activated yet. We’ve resent your activation link.</div>
                </div>
                <div className="ac-body">
                    <div className="ac-box ac-box-warn">
                    <div className="ac-box-title">Activation link resent</div>
                    <div className="ac-box-sub">Check your email for a new magic link. It expires in 72 hours.</div>
                    </div>
                    <div className="ac-field-link">
                    <Link to={`/learn/${clientSlug}`} onClick={() => setStatus('email-input')} className="ac-link">
                        ← Use a different email
                    </Link>
                    </div>
                </div>
                <div className="ac-footer" />
            </AuthCard>
        );
    }

    // A Deactivated account sees the agreed words only, as at every other step (S-6.1 pt 5, agreed 17 Oct). Removed 17
    // Oct 2026 (S-6.1 pt 5): "Account inactive. Your account has been not active yet." and its "Access restricted" box.
    if (status === 'inactive') {
        return (
            <AuthCard logoUrl={logoUrl}>
                <div className="ac-top">
                    <div className="ac-icon ac-icon-error">
                    <span style={{ fontSize: '24px' }}>
                        <CancelOutlinedIcon />
                    </span>
                    </div>
                    <div className="ac-title">Your account is inactive.</div>
                    <div className="ac-sub">Please contact your administrator.</div>
                </div>
                <div className="ac-body">
                    <div className="ac-field-link">
                    <Link to={`/learn/${clientSlug}`} onClick={() => setStatus('email-input')} className="ac-link">
                        ← Use a different email
                    </Link>
                    </div>
                </div>
                <div className="ac-footer" />
            </AuthCard>
        );
    }    

    return (
        <AuthCard logoUrl={logoUrl}>
            <div className="ac-top">
                <div className="ac-icon ac-icon-warn">
                    <span style={{ fontSize: '24px' }}>
                        <LockOutlinedIcon />
                    </span>
                </div>
                <div className="ac-title">Reset your password</div>
                <div className="ac-sub">Enter your email and we’ll send you a link to reset your password.</div>
            </div>

            <div className="ac-body">
                {message.text && (
                    <div className={`ac-box ${message.type === 'success' ? 'ac-box-success' : 'ac-box-error'}`}>
                        <div className="ac-box-sub">{message.text}</div>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="ac-field">
                        <div className="ac-field-label">Email</div>
                        <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="ac-field-input"
                        placeholder="name@company.com"
                        required
                        />
                    </div>

                    <button type="submit" className="ac-btn-primary" disabled={isLoading}>
                        {isLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send reset link'}
                    </button>
                </form>
                <div className="ac-field-link">
                    <Link to={`/learn/${clientSlug}`} onClick={() => setStatus('email-input')} className="ac-link">
                        ← Back to sign in
                    </Link>
                </div>
            </div>

            <div className="ac-footer" />
        </AuthCard>
    );
};

export default ForgotPasswordAuthCard;